import React, { useEffect, useState } from 'react'
import Icon from '../views/components/Icon'

interface AutoLogoutWarningProps {
  isOpen: boolean
  remainingSeconds: number
  onStayLoggedIn: () => void
  onLogout: () => void
}

/**
 * Modal that warns the user about impending auto-logout
 * Shows a countdown timer and allows the user to stay logged in
 */
const AutoLogoutWarning: React.FC<AutoLogoutWarningProps> = ({
  isOpen,
  remainingSeconds: initialSeconds,
  onStayLoggedIn,
  onLogout,
}: AutoLogoutWarningProps) => {
  const [seconds, setSeconds] = useState(initialSeconds)

  useEffect(() => {
    if (!isOpen) return

    setSeconds(initialSeconds)

    const interval = setInterval(() => {
      setSeconds((prev: number) => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isOpen, initialSeconds])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 space-y-4">
        {/* Icon */}
        <div className="flex items-center justify-center">
          <div className="bg-yellow-100 rounded-full p-3">
            <Icon type="clock" className="w-8 h-8 text-yellow-600" />
          </div>
        </div>

        {/* Content */}
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-gray-900">
            Session Timeout Warning
          </h2>
          <p className="text-gray-600">
            You will be automatically logged out due to inactivity in
          </p>
          <div className="text-4xl font-bold text-yellow-600">{seconds}</div>
          <p className="text-sm text-gray-500">seconds</p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onLogout}
            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Logout Now
          </button>
          <button
            onClick={onStayLoggedIn}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  )
}

export default AutoLogoutWarning
